export interface ServerSentEvent {
  data: string;
  event?: string;
  id?: string;
  retry?: number;
}

/**
 * Incremental Server-Sent Events parser.
 *
 * Fetch streams may split anywhere: between UTF-8 bytes, between CR/LF, or in
 * the middle of a field. This parser accepts decoded text incrementally and
 * only dispatches complete SSE records. It follows the EventSource field
 * rules, including multi-line data payloads and CR, LF, or CRLF line endings.
 */
export class ServerSentEventParser {
  private buffer = "";
  private dataLines: string[] = [];
  private eventName: string | undefined;
  private lastEventId: string | undefined;
  private retry: number | undefined;

  feed(chunk: string): ServerSentEvent[] {
    this.buffer += chunk;
    return this.consumeLines(false);
  }

  /** Flush a final unterminated record when the transport reaches EOF. */
  finish(chunk = ""): ServerSentEvent[] {
    this.buffer += chunk;
    const events = this.consumeLines(true);
    const finalEvent = this.dispatch();
    if (finalEvent) events.push(finalEvent);
    return events;
  }

  private consumeLines(flush: boolean): ServerSentEvent[] {
    const events: ServerSentEvent[] = [];

    while (this.buffer.length > 0) {
      const lineEnd = this.buffer.search(/[\r\n]/);
      if (lineEnd === -1) {
        if (!flush) break;
        const line = this.buffer;
        this.buffer = "";
        this.processLine(line, events);
        break;
      }

      const terminator = this.buffer[lineEnd];
      if (terminator === "\r" && lineEnd + 1 === this.buffer.length && !flush) {
        // The next chunk may begin with LF, so keep the CR until we know.
        break;
      }

      const terminatorLength =
        terminator === "\r" && this.buffer[lineEnd + 1] === "\n" ? 2 : 1;
      const line = this.buffer.slice(0, lineEnd);
      this.buffer = this.buffer.slice(lineEnd + terminatorLength);
      this.processLine(line, events);
    }

    return events;
  }

  private processLine(line: string, events: ServerSentEvent[]): void {
    if (line === "") {
      const event = this.dispatch();
      if (event) events.push(event);
      return;
    }

    if (line.startsWith(":")) return;

    const colonIndex = line.indexOf(":");
    const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
    let value = colonIndex === -1 ? "" : line.slice(colonIndex + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    switch (field) {
      case "data":
        this.dataLines.push(value);
        break;
      case "event":
        this.eventName = value;
        break;
      case "id":
        if (!value.includes("\0")) this.lastEventId = value;
        break;
      case "retry":
        if (/^\d+$/.test(value)) this.retry = Number(value);
        break;
      default:
        break;
    }
  }

  private dispatch(): ServerSentEvent | null {
    if (this.dataLines.length === 0) {
      this.eventName = undefined;
      this.retry = undefined;
      return null;
    }

    const event: ServerSentEvent = {
      data: this.dataLines.join("\n"),
    };
    if (this.eventName !== undefined) event.event = this.eventName;
    if (this.lastEventId !== undefined) event.id = this.lastEventId;
    if (this.retry !== undefined) event.retry = this.retry;

    this.dataLines = [];
    this.eventName = undefined;
    this.retry = undefined;
    return event;
  }
}

